import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import EmployeeLayout from '../../components/layout/EmployeeLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Clock,
  Camera,
  MapPin,
  Briefcase,
  CheckCircle2,
  AlertTriangle,
  Pencil,
  ChevronDown,
  ChevronUp,
  Search,
  Inbox,
  CalendarDays,
  Coffee,
  Info,
} from 'lucide-react';
import {
  format,
  parseISO,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
  isWithinInterval,
} from 'date-fns';
import { formatUK } from '../../lib/ukTime';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pretty-format minutes → "12h 41m" (or "0m" / "45m" / "8h").
const fmtHm = (minutes) => {
  if (!minutes || minutes <= 0) return '0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

// Break composition per the automatic rules — so a 12h shift clearly reads
// as "2 × 15 min + 1 × 30 min" rather than a bare "1h".
const breakComposition = (min) => {
  if (!min) return 'No break';
  if (min >= 60) return '2 × 15 min + 1 × 30 min';
  return `1 × ${min} min`;
};

export default function EmployeeHistoryPage() {
  const [events, setEvents] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [workedRows, setWorkedRows] = useState({});
  const [loading, setLoading] = useState(true);
  // Per-card expand/collapse state — keyed by composite "shiftKey" so the
  // expansion survives re-renders when filters change.
  const [expanded, setExpanded] = useState({});
  const [photoModal, setPhotoModal] = useState({ open: false, photo: null, loading: false, title: '' });
  const [filter, setFilter] = useState('all'); // all | current | previous
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [evRes, shRes] = await Promise.all([
          axios.get(`${API}/clock/history`, { params: { limit: 200 } }),
          axios.get(`${API}/shifts/my`),
        ]);
        setEvents(evRes.data || []);
        setShifts(shRes.data || []);
        try {
          const whRes = await axios.get(`${API}/users/me/worked-hours`);
          const m = {};
          (whRes.data?.rows || []).forEach((r) => {
            if (r.clock_in_event_id) m[r.clock_in_event_id] = r;
          });
          setWorkedRows(m);
        } catch (e) {
          console.warn('Worked-hours fetch failed:', e);
        }
      } catch (error) {
        console.error('Failed to fetch history:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Build a lookup so we can enrich each pair with position / location /
  // admin-edit metadata without a second roundtrip per card.
  const shiftMap = useMemo(() => {
    const m = {};
    shifts.forEach((s) => { if (s?.id) m[s.id] = s; });
    return m;
  }, [shifts]);

  // Pair clock_in / clock_out events into "shifts" for display. We prefer
  // pairing by shift_id (most reliable since the backend resolves it on
  // clock-in). When a clock_out arrives with no matching open clock_in
  // (e.g. orphaned auto-clock-out), we still surface it so nothing is lost.
  const pairs = useMemo(() => {
    // Sort chronologically ascending so the pairing walk is deterministic.
    const sorted = [...events].sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
    const result = [];
    const openByShift = {}; // shift_id → index into result

    sorted.forEach((ev) => {
      if (ev.event_type === 'clock_in') {
        const idx = result.push({
          key: ev.id,
          shift_id: ev.shift_id || null,
          clock_in: ev,
          clock_out: null,
        }) - 1;
        if (ev.shift_id) openByShift[ev.shift_id] = idx;
      } else if (ev.event_type === 'clock_out') {
        let targetIdx = ev.shift_id != null ? openByShift[ev.shift_id] : undefined;
        if (targetIdx === undefined) {
          // No shift link — mirror the backend pairing: close the most recent
          // still-open clock_in within 24h (manual / unscheduled shifts).
          for (let i = result.length - 1; i >= 0; i -= 1) {
            const r = result[i];
            if (r.clock_in && !r.clock_out) {
              const delta = new Date(ev.timestamp) - new Date(r.clock_in.timestamp);
              if (delta > 0 && delta <= 24 * 3600 * 1000) targetIdx = i;
              break;
            }
          }
        }
        if (targetIdx !== undefined && result[targetIdx] && !result[targetIdx].clock_out) {
          result[targetIdx].clock_out = ev;
          delete openByShift[ev.shift_id];
        } else {
          // Orphan clock_out — render on its own so it isn't hidden.
          result.push({
            key: ev.id,
            shift_id: ev.shift_id || null,
            clock_in: null,
            clock_out: ev,
          });
        }
      }
    });

    // Newest first for the UI.
    return result.reverse();
  }, [events]);

  // Enrich each pair with derived display data (date, position, status,
  // worked minutes, overnight flag). Doing it once here keeps the JSX
  // below cheap and easier to read.
  const enriched = useMemo(() => {
    return pairs.map((p) => {
      const anchor = p.clock_in || p.clock_out;
      const anchorTs = anchor?.timestamp;
      const dateStr = anchorTs ? formatUK(anchorTs, 'yyyy-MM-dd') : '';
      const shift = p.shift_id ? shiftMap[p.shift_id] : null;

      const position = shift?.position || shift?.role || anchor?.shift_role || '';
      const location = shift?.site_name || '';
      const notes = shift?.notes || '';
      const editedByAdmin = !!shift?.edited_by_admin;
      const autoClockOut = !!p.clock_out?.auto_clock_out;

      // Worked minutes — only when we have both ends. Crossing midnight
      // is handled naturally by Date subtraction (UTC arithmetic).
      let workedMin = 0;
      let overnight = false;
      if (p.clock_in?.timestamp && p.clock_out?.timestamp) {
        const a = new Date(p.clock_in.timestamp);
        const b = new Date(p.clock_out.timestamp);
        workedMin = Math.max(0, Math.round((b - a) / 60000));
        overnight = formatUK(p.clock_in.timestamp, 'yyyy-MM-dd') !==
                    formatUK(p.clock_out.timestamp, 'yyyy-MM-dd');
      }

      // Status precedence: Edited by Admin > Auto Clocked Out > Completed > In Progress
      let status = 'in_progress';
      if (p.clock_in && p.clock_out) {
        if (editedByAdmin) status = 'edited';
        else if (autoClockOut) status = 'auto_out';
        else status = 'completed';
      }

      return { ...p, dateStr, position, location, notes, editedByAdmin, autoClockOut, workedMin, overnight, status,
        wh: (p.clock_in?.id && p.clock_out) ? workedRows[p.clock_in.id] || null : null };
    });
  }, [pairs, shiftMap, workedRows]);

  // ---- Filtering ----
  const filtered = useMemo(() => {
    const now = new Date();
    const curStart = startOfMonth(now);
    const curEnd = endOfMonth(now);
    const prevStart = startOfMonth(subMonths(now, 1));
    const prevEnd = endOfMonth(subMonths(now, 1));
    return enriched.filter((p) => {
      if (!p.dateStr) return false;
      const d = parseISO(p.dateStr);
      if (filter === 'current' && !isWithinInterval(d, { start: curStart, end: curEnd })) return false;
      if (filter === 'previous' && !isWithinInterval(d, { start: prevStart, end: prevEnd })) return false;
      if (search.trim()) {
        // Match either the ISO date OR the pretty "dd mmm yyyy" rendering.
        const pretty = format(d, 'dd MMM yyyy').toLowerCase();
        const q = search.trim().toLowerCase();
        if (!p.dateStr.includes(q) && !pretty.includes(q)) return false;
      }
      return true;
    });
  }, [enriched, filter, search]);

  // ---- Totals (computed off the FULL enriched set so summary stays
  // accurate even when the user narrows down via filters/search). ----
  const totals = useMemo(() => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);
    let weekMin = 0;
    let monthMin = 0;
    enriched.forEach((p) => {
      if (!p.workedMin) return;
      const d = parseISO(p.dateStr);
      if (isWithinInterval(d, { start: weekStart, end: weekEnd })) weekMin += p.workedMin;
      if (isWithinInterval(d, { start: monthStart, end: monthEnd })) monthMin += p.workedMin;
    });
    return { weekMin, monthMin };
  }, [enriched]);

  // ---- Gross / Break / Net totals for the ACTIVE filter (All / Current /
  // Previous month) — values come from the backend central calculation. ----
  const whTotals = useMemo(() => {
    let gross = 0, brk = 0, net = 0, n = 0;
    filtered.forEach((p) => {
      if (!p.wh) return;
      gross += p.wh.gross_hours;
      brk += p.wh.break_minutes;
      net += p.wh.net_hours;
      n += 1;
    });
    return { grossMin: Math.round(gross * 60), breakMin: brk, netMin: Math.round(net * 60), count: n };
  }, [filtered]);

  const handleViewPhoto = async (eventId, label) => {
    setPhotoModal({ open: true, photo: null, loading: true, title: label });
    try {
      const response = await axios.get(`${API}/admin/clock/event-photo/${eventId}`);
      setPhotoModal({ open: true, photo: response.data.photo_url, loading: false, title: label });
    } catch (error) {
      setPhotoModal({ open: true, photo: null, loading: false, title: label });
      toast.error('Failed to load photo');
    }
  };

  const toggleExpand = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  if (loading) {
    return (
      <EmployeeLayout>
        <div className="py-8 flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </EmployeeLayout>
    );
  }

  return (
    <EmployeeLayout>
      <div className="py-6 px-3 sm:px-4 space-y-5 max-w-3xl mx-auto">
        <div className="text-center">
          <h1 className="text-xl font-barlow tracking-widest flex items-center justify-center gap-2">
            <Clock className="w-5 h-5 text-primary" strokeWidth={1.5} />
            CLOCK HISTORY
          </h1>
        </div>

        {/* Summary cards: total worked time this week / this month */}
        <div className="grid grid-cols-2 gap-3" data-testid="history-summary">
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-4">
              <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">This Week</p>
              <p className="text-xl font-semibold font-mono mt-1" data-testid="total-week">
                {fmtHm(totals.weekMin)}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-4">
              <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">This Month</p>
              <p className="text-xl font-semibold font-mono mt-1" data-testid="total-month">
                {fmtHm(totals.monthMin)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters + Search */}
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {[
              { key: 'all', label: 'All Records' },
              { key: 'current', label: 'Current Month' },
              { key: 'previous', label: 'Previous Month' },
            ].map((f) => (
              <Button
                key={f.key}
                size="sm"
                variant={filter === f.key ? 'default' : 'outline'}
                onClick={() => setFilter(f.key)}
                className="rounded-sm text-xs h-8 px-3"
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </Button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by date (e.g. 2026-06 or 21 Jun)"
              className="pl-9 bg-background border-border rounded-sm h-9 text-sm"
              data-testid="history-search"
            />
          </div>
        </div>

        {/* Gross / Break / Net totals — follow the active filter below */}
        <Card className="bg-card border-border rounded-sm" data-testid="worked-totals">
          <CardContent className="p-4">
            <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">
              {filter === 'current' ? 'Current Month' : filter === 'previous' ? 'Previous Month' : 'All Records'} — {whTotals.count} completed shift{whTotals.count === 1 ? '' : 's'}
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Gross</p>
                <p className="font-mono text-base" data-testid="totals-gross">{fmtHm(whTotals.grossMin)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Breaks</p>
                <p className="font-mono text-base text-orange-400" data-testid="totals-break">{fmtHm(whTotals.breakMin)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Net Worked</p>
                <p className="font-mono text-base text-primary" data-testid="totals-net">{fmtHm(whTotals.netMin)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Break policy note */}
        <div className="flex items-start gap-2 text-[11px] text-muted-foreground border border-border rounded-sm px-3 py-2 bg-muted/20" data-testid="break-policy-note">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <p>
            Unpaid breaks are deducted automatically from completed shifts:
            under 4h — no break · 4–6h — 20 min · 6–8h — 30 min ·
            over 8h (e.g. a 12h shift) — <span className="text-foreground">1h total (2 × 15 min + 1 × 30 min)</span>.
            Net Worked = time between Clock In and Clock Out minus the break.
          </p>
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <Card className="bg-card border-border rounded-sm" data-testid="history-empty">
            <CardContent className="p-8 text-center text-muted-foreground space-y-2">
              <Inbox className="w-10 h-10 mx-auto opacity-50" />
              <p className="text-sm">No records found</p>
              <p className="text-xs">
                {search || filter !== 'all'
                  ? 'Try clearing filters or search to see more.'
                  : 'Your clock in/out records will appear here.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((p) => (
              <ShiftCard
                key={p.key}
                pair={p}
                expanded={!!expanded[p.key]}
                onToggle={() => toggleExpand(p.key)}
                onViewPhoto={handleViewPhoto}
              />
            ))}
          </div>
        )}

        {/* Photo Modal */}
        <Dialog open={photoModal.open} onOpenChange={(open) => setPhotoModal((prev) => ({ ...prev, open }))}>
          <DialogContent className="bg-card border-border rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary" />
                {photoModal.title || 'Clock Photo'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {photoModal.loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : photoModal.photo && photoModal.photo !== 'has_photo' ? (
                <div className="relative aspect-video bg-muted rounded-sm overflow-hidden">
                  <img src={photoModal.photo} alt="Clock photo" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="py-12 text-center">
                  <Camera className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Photo not available</p>
                </div>
              )}
              <Button
                variant="outline"
                onClick={() => setPhotoModal({ open: false, photo: null, loading: false, title: '' })}
                className="w-full border-border rounded-sm"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </EmployeeLayout>
  );
}

// ---------- Status badge mapper ----------
function StatusBadge({ status }) {
  const cfg = {
    completed: { label: 'Completed', icon: CheckCircle2, cls: 'bg-green-500/10 text-green-400 border-green-500/30' },
    auto_out: { label: 'Auto Clocked Out', icon: AlertTriangle, cls: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
    edited: { label: 'Edited by Admin', icon: Pencil, cls: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
    in_progress: { label: 'In Progress', icon: Clock, cls: 'bg-muted/30 text-muted-foreground border-border' },
  }[status] || { label: status, icon: Clock, cls: 'bg-muted/30 text-muted-foreground border-border' };
  const Icon = cfg.icon;
  return (
    <Badge className={`text-[10px] uppercase tracking-wider border ${cfg.cls} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </Badge>
  );
}

// ---------- Location row (employee history) ----------
// Renders either a "View on Map" link (Google Maps deeplink) when a pin
// was captured, or a muted "Location not provided" line so the employee
// can still see at a glance whether GPS was off / denied for that event.
function LocationRow({ label, event }) {
  if (!event) return null;
  const lat = event.latitude;
  const lng = event.longitude;
  const hasPin = typeof lat === 'number' && typeof lng === 'number';
  return (
    <div className="flex items-start gap-2 text-xs">
      <MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
        {hasPin ? (
          <a
            href={`https://www.google.com/maps?q=${lat},${lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline font-mono"
          >
            {lat.toFixed(5)}, {lng.toFixed(5)} — View on Map
          </a>
        ) : (
          <span className="text-muted-foreground italic">Location not provided</span>
        )}
        {hasPin && typeof event.accuracy === 'number' && (
          <span className="block text-[10px] text-muted-foreground/70">
            Accuracy: ±{Math.round(event.accuracy)} m
          </span>
        )}
      </div>
    </div>
  );
}

// ---------- Single shift card ----------
function ShiftCard({ pair, expanded, onToggle, onViewPhoto }) {
  const inT = pair.clock_in?.timestamp;
  const outT = pair.clock_out?.timestamp;
  const dateLabel = pair.dateStr ? format(parseISO(pair.dateStr), 'EEE, dd MMM yyyy') : '—';

  return (
    <Card className="bg-card border-border rounded-sm" data-testid={`shift-card-${pair.key}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left"
        data-testid={`shift-toggle-${pair.key}`}
        aria-expanded={expanded}
      >
        <CardContent className="p-4 space-y-2">
          {/* Header row: date + status */}
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-barlow tracking-wider uppercase text-muted-foreground flex items-center gap-1.5">
              <CalendarDays className="w-3.5 h-3.5" />
              {dateLabel}
            </p>
            <StatusBadge status={pair.status} />
          </div>

          {/* Times row */}
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span className="font-mono">{inT ? formatUK(inT, 'HH:mm') : '— —'}</span>
            <span className="text-muted-foreground">→</span>
            <span className="font-mono">{outT ? formatUK(outT, 'HH:mm') : '— —'}</span>
            {pair.overnight && (
              <Badge
                className="text-[10px] uppercase tracking-wider bg-purple-500/10 text-purple-300 border-purple-500/30"
                data-testid={`overnight-${pair.key}`}
              >
                +1 day
              </Badge>
            )}
            {!!pair.workedMin && (
              <span className="ml-auto text-xs text-muted-foreground" data-testid={`worked-${pair.key}`}>
                {pair.wh ? (
                  <>Net: <span className="font-mono text-primary">{fmtHm(Math.round(pair.wh.net_hours * 60))}</span></>
                ) : (
                  <>Worked: <span className="font-mono text-foreground">{fmtHm(pair.workedMin)}</span></>
                )}
              </span>
            )}
          </div>

          {/* Gross / Break / Net strip — automatic break, same central rules as payroll */}
          {pair.wh && (
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap" data-testid={`wh-strip-${pair.key}`}>
              <span>Gross: <span className="font-mono text-foreground">{fmtHm(Math.round(pair.wh.gross_hours * 60))}</span></span>
              <span className="flex items-center gap-1">
                <Coffee className="w-3 h-3" />
                Break: <span className="font-mono text-orange-400">{fmtHm(pair.wh.break_minutes)}</span>
                <span className="text-muted-foreground/70">({breakComposition(pair.wh.break_minutes)})</span>
              </span>
            </div>
          )}

          {/* Position + chevron */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-sm text-foreground min-w-0">
              <Briefcase className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="truncate">{pair.position || '—'}</span>
            </div>
            {expanded
              ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
              : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
          </div>
        </CardContent>
      </button>

      {expanded && (
        <div className="border-t border-border px-4 py-3 space-y-3" data-testid={`shift-expanded-${pair.key}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div className="space-y-0.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Position</p>
              <p>{pair.position || '—'}</p>
            </div>
            <div className="space-y-0.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Location</p>
              <p className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                {pair.location || '—'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {pair.clock_in?.photo_url && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onViewPhoto(pair.clock_in.id, 'Clock In Photo')}
                className="border-border rounded-sm text-xs h-8"
                data-testid={`view-in-photo-${pair.key}`}
              >
                <Camera className="w-3.5 h-3.5 mr-1.5" />
                View Clock In Photo
              </Button>
            )}
            {pair.clock_out?.photo_url && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onViewPhoto(pair.clock_out.id, 'Clock Out Photo')}
                className="border-border rounded-sm text-xs h-8"
                data-testid={`view-out-photo-${pair.key}`}
              >
                <Camera className="w-3.5 h-3.5 mr-1.5" />
                View Clock Out Photo
              </Button>
            )}
          </div>

          {/* Captured location pins. Each clock event has its own pin since
              employee may have clocked in/out from different places (e.g.
              picked up by transport). Falls back to "Location not provided"
              when the user denied GPS permission or the device returned no
              fix at capture time. */}
          <LocationRow label="Clock In Location" event={pair.clock_in} />
          <LocationRow label="Clock Out Location" event={pair.clock_out} />

          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Status</p>
            <StatusBadge status={pair.status} />
          </div>

          {pair.editedByAdmin && pair.notes && (
            <div className="space-y-1 border-l-2 border-blue-500/40 pl-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Notes</p>
              <p className="text-sm text-foreground/90 whitespace-pre-wrap">{pair.notes}</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
